import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { StatusBadge, PodBadge } from "@/components/StatusBadge";
import { Truck, PackageCheck, FileText } from "lucide-react";

export default function TrackPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/track/${token}`).then((r) => setData(r.data)).catch(() => setErr("Invalid or expired tracking link."));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-600">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[var(--navy)] text-white py-8">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-2 text-amber-400"><Truck size={18} /><span className="text-xs uppercase tracking-[0.2em] font-semibold">SRAD Dispatch Hub · ProcurMart</span></div>
          <h1 className="font-heading text-3xl font-extrabold mt-3">Dispatch Tracking</h1>
          <div className="font-mono text-sm text-slate-300 mt-1">{data.dispatch_code}</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-5">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase text-slate-500 tracking-widest">Current Status</div>
              <div className="mt-2"><StatusBadge status={data.delivery_status} /></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase text-slate-500 tracking-widest">POD</div>
              <div className="mt-2"><PodBadge status={data.pod_status} /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="Client" v={data.client?.name} />
            <Row label="Transporter" v={data.transporter?.name} />
            <Row label="Invoice No" v={data.invoice_number} />
            <Row label="Invoice Date" v={data.invoice_date} />
            <Row label="PO Number" v={data.client_po_number || "-"} />
            <Row label="LR / Tracking" v={data.lr_number} />
            <Row label="Dispatch Date" v={data.dispatch_date} />
            <Row label="Expected Delivery" v={data.expected_delivery || "-"} />
            <Row label="Actual Delivery" v={data.actual_delivery || "-"} />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="text-xs uppercase text-slate-500 tracking-widest mb-3">Documents</div>
          <div className="space-y-2">
            {data.documents?.map((d) => (
              <a key={d.id} href={`${API}/track/${token}/document/${d.id}`} target="_blank" rel="noreferrer"
                 className="flex items-center gap-3 border border-slate-200 rounded-md px-3 py-2.5 hover:bg-slate-50">
                <FileText size={16} className="text-slate-500" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{d.doc_type}</div>
                  <div className="text-xs text-slate-500">{d.original_filename}</div>
                </div>
                <span className="text-xs text-amber-700 font-semibold">View →</span>
              </a>
            ))}
            {(!data.documents || data.documents.length === 0) && <div className="text-sm text-slate-500">No documents available.</div>}
          </div>
        </div>

        <div className="text-center text-xs text-slate-400">
          SRAD SERVICES PRIVATE LIMITED · Powered by ProcurMart
        </div>
      </div>
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium">{v || "-"}</div>
    </div>
  );
}
